import type { AdminApiInContract, CreateDoctorAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import type { LegacyLicense } from '../helpers/licenseShapeAdapter'

/**
 * Builds the create-member request bodies for the new-member (create) page. This mirrors
 * exactly the body-shaping the legacy `onClickSafeMemberButton` performed in its create
 * branch — extracted here so the React Query create hook owns it without the Redux dispatch
 * side effects. The legacy edit branch still lives in `onClickSafeMemberButton` for member.jsx.
 */

export interface MemberSharedFields {
  email: string
  firstName: string
  lastName: string
  prefix: string
  suffix: string
}

export interface ProviderExtraFields {
  phoneNumber: { Number: string; Prefix: string; CountryCode: string } | null
  professionalTitle: string
  isExcludedFromMarketingReports: boolean
  npi: string
  productivityData: CreateDoctorAdminApiInContract['ProductivityOverrides']
  isProviderWithLicense: boolean
  licensesArray: LegacyLicense[]
}

export const buildCreateAdminRequest = (fields: MemberSharedFields): AdminApiInContract => ({
  Email: fields.email,
  FirstName: fields.firstName,
  LastName: fields.lastName,
  Prefix: fields.prefix?.trim(),
  Suffix: fields.suffix?.trim(),
})

export const buildCreateProviderRequest = (
  fields: MemberSharedFields,
  extra: ProviderExtraFields,
): CreateDoctorAdminApiInContract => {
  const licenses: CreateDoctorAdminApiInContract['Licenses'] = extra.licensesArray.map((t) => ({
    VisitTypes: t.licenseVisitTypesOptions
      .map((item) =>
        item.isSelected && item.visibilityType ?
          { VisitTypeId: item.VisitTypeId, VisibilityType: item.visibilityType.name }
        : null,
      )
      .filter((serviceType): serviceType is { VisitTypeId: string; VisibilityType: string } => serviceType !== null),
    Number: t.licenseNumberValue ?? '',
    StateCode: t.stateLicenseValue ? t.stateLicenseValue.slice(-2) : '',
    CslNumber: t.cslNumber ?? null,
    DeaNumber: t.deaNumber ?? null,
    DeaStateCode: t.deaStateCode ?? null,
  }))

  return {
    PhoneNumber: extra.phoneNumber,
    Email: fields.email,
    FirstName: fields.firstName,
    LastName: fields.lastName,
    Prefix: fields.prefix?.trim() ?? null,
    Suffix: fields.suffix?.trim() ?? null,
    ProfessionalTitles: extra.professionalTitle.split(';').map((t) => t.trim()),
    IsExcludedFromMarketingReports: extra.isExcludedFromMarketingReports,
    NationalProviderIdentifier: extra.npi || null,
    ProductivityOverrides: extra.productivityData ?? null,
    Licenses: extra.isProviderWithLicense ? licenses : null,
  }
}
